import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  TrendingUp, 
  CheckCircle, 
  Smile, 
  Calendar,
  Plus,
  ArrowRight,
  Bell,
  Check,
  AlertTriangle,
  Star,
  ClipboardList,
  Volume2,
  Utensils,
  Flame,
  Target,
  CircleDot,
  Sparkles,
  ChevronRight,
  BarChart3
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePeriod } from "@/hooks/usePeriod";
import { useReminderNotifications, requestNotificationPermission, testNotificationSound } from "@/hooks/useReminders";

const MOOD_EMOJIS = ["😢", "😕", "😐", "🙂", "😊"];

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { periodStartDate } = usePeriod();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [priorityTasks, setPriorityTasks] = useState([]);
  const [nutritionSummary, setNutritionSummary] = useState(null);
  const [needsStatus, setNeedsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  
  // Quick expense dialog
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [quickExpense, setQuickExpense] = useState({ description: "", amount: "", category: "A" });

  useReminderNotifications(notificationsEnabled);

  const today = new Date();
  const formattedDate = today.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long"
  });

  // Motivational phrases
  const motivationalPhrases = [
    "Ogni giorno è un nuovo inizio",
    "Un passo alla volta",
    "Sei sulla strada giusta",
    "Oggi è pieno di possibilità",
    "Concentrati su ciò che conta"
  ];
  const dailyPhrase = motivationalPhrases[today.getDate() % motivationalPhrases.length];

  useEffect(() => {
    fetchAllData();
    if ("Notification" in window && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, remindersRes, urgentRes, priorityRes, nutritionRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/reminders'),
        api.get('/life-tasks/urgent'),
        api.get('/life-tasks/priorities'),
        api.get(`/nutrition/daily-summary?date=${today.toISOString().split("T")[0]}`)
      ]);
      
      setStats(statsRes.data);
      setUrgentTasks(urgentRes.data || []);
      setPriorityTasks(priorityRes.data || []);
      setNutritionSummary(nutritionRes.data);
      
      // Filter upcoming reminders
      const upcoming = (remindersRes.data || [])
        .filter(r => !r.is_completed && r.datetime_iso >= new Date().toISOString())
        .sort((a, b) => a.datetime_iso.localeCompare(b.datetime_iso));
      setReminders(upcoming);
      
      // Fetch needs status
      try {
        const needsRes = await api.get('/needs');
        const unsatisfiedCount = (needsRes.data || []).filter(n => n.status !== 'satisfied').length;
        setNeedsStatus({ total: 10, unsatisfied: unsatisfiedCount });
      } catch (e) {
        setNeedsStatus(null);
      }
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/life-tasks/${taskId}/complete`);
      toast.success("Completato!");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/complete`);
      toast.success("Promemoria completato");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleMoodSelect = async (level) => {
    try {
      await api.post('/moods', {
        date: today.toISOString().split("T")[0],
        level: level + 1
      });
      toast.success("Umore registrato!");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleQuickExpense = async (e) => {
    e.preventDefault();
    if (!quickExpense.description || !quickExpense.amount) return;

    try {
      await api.post('/expenses', {
        description: quickExpense.description,
        amount: parseFloat(quickExpense.amount),
        category: quickExpense.category,
        date: today.toISOString().split("T")[0]
      });
      toast.success("Spesa aggiunta!");
      setQuickExpense({ description: "", amount: "", category: "A" });
      setExpenseDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setNotificationsEnabled(true);
      toast.success("Notifiche abilitate!");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto" data-testid="dashboard">
      
      {/* ========== SEZIONE 1: CARD OGGI ========== */}
      <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-white/70 text-sm font-medium uppercase tracking-wide">Oggi</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">
              Ciao, {user?.name?.split(" ")[0] || "amico"}! 👋
            </h1>
            <p className="text-white/80 mt-1 capitalize">{formattedDate}</p>
            <p className="text-white/60 text-sm mt-2 italic">"{dailyPhrase}"</p>
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-2 sm:gap-3">
            <Button 
              onClick={() => navigate('/compiti')}
              className="bg-white/20 hover:bg-white/30 text-white border-0 rounded-xl backdrop-blur-sm"
              data-testid="quick-task-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Compito
            </Button>
            
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white/20 hover:bg-white/30 text-white border-0 rounded-xl backdrop-blur-sm" data-testid="quick-expense-btn">
                  <Plus className="w-4 h-4 mr-1" /> Spesa
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader>
                  <DialogTitle>Aggiungi Spesa</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleQuickExpense} className="space-y-4 pt-4">
                  <Input
                    placeholder="Descrizione"
                    value={quickExpense.description}
                    onChange={(e) => setQuickExpense({ ...quickExpense, description: e.target.value })}
                    className="rounded-xl"
                  />
                  <div className="flex gap-3">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="€ Importo"
                      value={quickExpense.amount}
                      onChange={(e) => setQuickExpense({ ...quickExpense, amount: e.target.value })}
                      className="rounded-xl flex-1"
                    />
                    <Select value={quickExpense.category} onValueChange={(v) => setQuickExpense({ ...quickExpense, category: v })}>
                      <SelectTrigger className="w-24 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="I">Indispensabile</SelectItem>
                        <SelectItem value="A">Accessorio</SelectItem>
                        <SelectItem value="X">Imprevisto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full rounded-xl">Aggiungi</Button>
                </form>
              </DialogContent>
            </Dialog>
            
            <Button 
              onClick={() => document.getElementById('mood-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/20 hover:bg-white/30 text-white border-0 rounded-xl backdrop-blur-sm"
              data-testid="quick-mood-btn"
            >
              <Smile className="w-4 h-4 mr-1" /> Umore
            </Button>
          </div>
        </div>
      </div>

      {/* ========== BANNER TRAINING ASSERTIVITÀ ========== */}
      <Link to="/assertivita" className="block">
        <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500 rounded-2xl p-4 sm:p-5 text-white shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-white/90 text-sm sm:text-base">
                  Sei iscritto al training Assertività e Comunicazione Efficace?
                </p>
                <p className="text-white/70 text-xs sm:text-sm mt-0.5">
                  Clicca qui per misurare il tuo livello di assertività
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
        </div>
      </Link>

      {/* ========== SEZIONE 2: PROGRESSI (3 mini card) ========== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        
        {/* Mini Card: Abitudini */}
        <Link to="/abitudini" className="block">
          <div className="soft-card p-5 hover:shadow-lg hover:-translate-y-1 transition-all group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats?.habits?.done_this_month || 0}</p>
            <p className="text-sm text-gray-500">Abitudini completate</p>
            <p className="text-xs text-gray-400 mt-1">questo mese</p>
          </div>
        </Link>
        
        {/* Mini Card: Bisogni */}
        <Link to="/bisogni" className="block">
          <div className="soft-card p-5 hover:shadow-lg hover:-translate-y-1 transition-all group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <CircleDot className="w-5 h-5 text-purple-600" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
            {needsStatus ? (
              <>
                <p className="text-2xl font-bold text-gray-800">{10 - needsStatus.unsatisfied}/10</p>
                <p className="text-sm text-gray-500">Bisogni soddisfatti</p>
                {needsStatus.unsatisfied > 0 && (
                  <p className="text-xs text-amber-500 mt-1">{needsStatus.unsatisfied} da curare</p>
                )}
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-800">—</p>
                <p className="text-sm text-gray-500">Ruota dei bisogni</p>
                <p className="text-xs text-gray-400 mt-1">Fai il check-in</p>
              </>
            )}
          </div>
        </Link>
        
        {/* Mini Card: Calorie */}
        <Link to="/alimentazione" className="block">
          <div className="soft-card p-5 hover:shadow-lg hover:-translate-y-1 transition-all group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
            </div>
            {nutritionSummary ? (
              <>
                <p className="text-2xl font-bold text-gray-800">
                  {nutritionSummary.totals.calories.toFixed(0)}
                  <span className="text-sm font-normal text-gray-400">/{nutritionSummary.goal}</span>
                </p>
                <Progress 
                  value={Math.min((nutritionSummary.totals.calories / nutritionSummary.goal) * 100, 100)}
                  className="h-1.5 mt-2"
                />
                <p className="text-xs text-gray-400 mt-1">kcal oggi</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-800">0</p>
                <p className="text-sm text-gray-500">Calorie oggi</p>
                <p className="text-xs text-gray-400 mt-1">Inizia a tracciare</p>
              </>
            )}
          </div>
        </Link>
      </div>

      {/* ========== SEZIONE 3: ORGANIZZAZIONE ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* Card: Promemoria */}
        <div className="soft-card overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-orange-500" />
              <span className="font-semibold text-gray-700">Promemoria</span>
            </div>
            {!notificationsEnabled && (
              <button onClick={handleEnableNotifications} className="text-xs text-orange-600 hover:underline">
                Abilita notifiche
              </button>
            )}
          </div>
          <div className="p-4">
            {reminders.length > 0 ? (
              <ul className="space-y-3">
                {reminders.slice(0, 3).map((reminder) => {
                  const remDate = new Date(reminder.datetime_iso);
                  return (
                    <li key={reminder.reminder_id} className="flex items-center gap-3 group">
                      <button
                        onClick={() => handleCompleteReminder(reminder.reminder_id)}
                        className="w-5 h-5 rounded-full border-2 border-orange-300 hover:border-green-500 hover:bg-green-50 flex-shrink-0 transition-colors"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{reminder.title}</p>
                        <p className="text-xs text-gray-400">
                          {remDate.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} {remDate.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="empty-state py-6">
                <p className="empty-state-icon">🔔</p>
                <p className="empty-state-text">Nessun promemoria attivo</p>
              </div>
            )}
            <Link to="/calendario">
              <Button variant="ghost" size="sm" className="w-full mt-3 text-gray-500 hover:text-gray-700 rounded-xl">
                Gestisci <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Card: Appuntamenti */}
        <div className="soft-card overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-500" />
            <span className="font-semibold text-gray-700">Appuntamenti</span>
          </div>
          <div className="p-4">
            {stats?.appointments?.length > 0 ? (
              <ul className="space-y-3">
                {stats.appointments.slice(0, 3).map((apt) => (
                  <li key={apt.appointment_id} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex flex-col items-center justify-center text-blue-600 flex-shrink-0">
                      <span className="text-xs font-bold leading-none">
                        {new Date(apt.date).toLocaleDateString("it-IT", { day: "numeric" })}
                      </span>
                      <span className="text-[10px] uppercase">
                        {new Date(apt.date).toLocaleDateString("it-IT", { month: "short" })}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 truncate">{apt.title}</p>
                      {apt.time && <p className="text-xs text-gray-400">{apt.time}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty-state py-6">
                <p className="empty-state-icon">📅</p>
                <p className="empty-state-text">Nessun appuntamento in programma</p>
              </div>
            )}
            <Link to="/calendario">
              <Button variant="ghost" size="sm" className="w-full mt-3 text-gray-500 hover:text-gray-700 rounded-xl">
                Vai al calendario <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Card: Compiti di Vita */}
        <div className="soft-card overflow-hidden lg:row-span-2">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-gray-700">Compiti di Vita</span>
          </div>
          <div className="p-4 space-y-4">
            
            {/* Subcard: Urgenze */}
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="accent-badge-urgent">
                    <AlertTriangle className="w-3 h-3" /> Urgente
                  </span>
                </div>
                <span className="text-xs text-gray-400">{urgentTasks.length} task</span>
              </div>
              <div className="p-3">
                {urgentTasks.length > 0 ? (
                  <ul className="space-y-2">
                    {urgentTasks.slice(0, 3).map(task => (
                      <li key={task.task_id} className="flex items-center gap-2 group">
                        <button
                          onClick={() => handleCompleteTask(task.task_id)}
                          className="w-4 h-4 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex-shrink-0 transition-all"
                        />
                        <span className="text-sm text-gray-700 truncate flex-1">{task.title}</span>
                        {task.due_date && (
                          <span className="text-[10px] text-red-500">
                            {new Date(task.due_date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-green-600 text-center py-2">Zero urgenze 🎉</p>
                )}
              </div>
            </div>
            
            {/* Subcard: Top 3 */}
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="accent-badge-priority">
                    <Star className="w-3 h-3" /> Top 3
                  </span>
                </div>
                <span className="text-xs text-gray-400">{priorityTasks.length}/3</span>
              </div>
              <div className="p-3">
                {priorityTasks.length > 0 ? (
                  <ul className="space-y-2">
                    {priorityTasks.map((task, idx) => (
                      <li key={task.task_id} className="flex items-center gap-2 group">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${
                          idx === 0 ? 'bg-amber-400' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
                        }`}>
                          {idx + 1}
                        </span>
                        <button
                          onClick={() => handleCompleteTask(task.task_id)}
                          className="w-4 h-4 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex-shrink-0 transition-all"
                        />
                        <span className="text-sm text-gray-700 truncate flex-1">{task.title}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-sm text-gray-400 mb-2">Scegli i tuoi 3 focus</p>
                    <Link to="/compiti">
                      <Button size="sm" variant="outline" className="rounded-full text-xs">
                        Imposta Top 3
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* CTA */}
            <div className="flex gap-2">
              <Link to="/compiti" className="flex-1">
                <Button className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid="go-to-tasks">
                  Apri Compiti
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Card: Spese */}
        <div className="soft-card overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="font-semibold text-gray-700">Spese Settimanali</span>
          </div>
          <div className="p-4">
            <p className="text-3xl font-bold text-gray-800">
              €{stats?.expenses?.total_week?.toFixed(2) || "0.00"}
            </p>
            <div className="flex gap-3 mt-2 text-xs">
              {Object.entries(stats?.expenses?.by_category || {}).map(([cat, amount]) => (
                <div key={cat} className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${
                    cat === "I" ? "bg-gray-800" : cat === "A" ? "bg-gray-400" : "bg-red-500"
                  }`} />
                  <span className="text-gray-500">{cat}: €{amount?.toFixed(0)}</span>
                </div>
              ))}
            </div>
            <Link to="/spese">
              <Button variant="ghost" size="sm" className="w-full mt-3 text-gray-500 hover:text-gray-700 rounded-xl">
                Vedi tutte <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Card: Umore */}
        <div className="soft-card overflow-hidden" id="mood-section">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Smile className="w-4 h-4 text-yellow-500" />
            <span className="font-semibold text-gray-700">Come ti senti oggi?</span>
          </div>
          <div className="p-4">
            <div className="flex justify-between">
              {MOOD_EMOJIS.map((emoji, idx) => (
                <button
                  key={`mood-${emoji}-${idx}`}
                  onClick={() => handleMoodSelect(idx)}
                  data-testid={`mood-btn-${idx}`}
                  className={`w-11 h-11 rounded-full border-2 flex items-center justify-center text-xl transition-all duration-200 hover:scale-110 ${
                    stats?.mood?.level === idx + 1 
                      ? "border-indigo-500 bg-indigo-50 scale-110 shadow-md" 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {stats?.mood?.level && (
              <p className="text-xs text-center text-gray-400 mt-3">
                Registrato: {MOOD_EMOJIS[stats.mood.level - 1]}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
